import React, { useEffect, useState } from "react";
import { Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { Badge, Panel, borderStyle, inputCls } from "../ui";

/** Email wiring: where notifications go, and a one-at-a-time test send. */
export default function EmailPanel() {
  const [status, setStatus] = useState(null);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState("");

  const load = () =>
    adminApi.get("/admin/email/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));

  useEffect(() => { load(); }, []);

  async function sendTest(kind) {
    if (!to.includes("@")) {
      toast.error("Enter an email address to send the test to");
      return;
    }
    setBusy(kind);
    try {
      const { data } = await adminApi.post("/admin/email/test", { to, kind });
      toast.success(`Test ${kind} email sent`, {
        description: `Sent to ${data.to}`,
      });
      load();
    } catch (err) {
      toast.error("Could not send", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy("");
    }
  }

  return (
    <Panel className="lg:col-span-2">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider gold-text">
        <Mail className="h-4 w-4" /> Email notifications
      </h2>

      {!status ? (
        <p className="text-sm text-[color:var(--nq-stone)]">Checking...</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            {status.configured ? <Badge tone="green">connected</Badge> : <Badge tone="red">not configured</Badge>}
            <span className="text-xs text-[color:var(--nq-stone)]">
              Sends from <strong>{status.sends_from || "-"}</strong> to{" "}
              <strong>{status.staff_inbox || "-"}</strong>
            </span>
            {status.build_sheets_go_to?.length > 0 && (
              <span className="text-xs text-[color:var(--nq-stone)]" data-testid="email-build-inbox">
                Build sheets to <strong>{status.build_sheets_go_to.join(", ")}</strong>
              </span>
            )}
          </div>

          <p className="mt-3 text-xs text-[color:var(--nq-stone)]">
            Paid orders email the staff inbox with the customer, address, items and total, and send
            the buyer a receipt. Every website enquiry emails the staff inbox with Reply-To set to
            the customer, so hitting Reply answers them directly.

          </p>

          <div className="mt-4 flex flex-wrap items-end gap-2">
            <input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="send a test to..."
              className={`${inputCls} max-w-xs`}
              style={borderStyle}
              data-testid="email-test-to"
            />
            <button
              onClick={() => sendTest("enquiry")}
              disabled={!!busy}
              className="btn-ghost inline-flex items-center gap-2 text-xs"
              data-testid="email-test-enquiry"
            >
              {busy === "enquiry" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Test enquiry email
            </button>
            <button
              onClick={() => sendTest("order")}
              disabled={!!busy}
              className="btn-ghost inline-flex items-center gap-2 text-xs"
              data-testid="email-test-order"
            >
              {busy === "order" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Test order email
            </button>
          </div>

          {(status.recent || []).length > 0 && (
            <div className="mt-4">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-[color:var(--nq-stone)]">
                Last sends
              </p>
              <ul className="space-y-0.5 text-xs text-[color:var(--nq-stone)]">
                {status.recent.slice(0, 5).map((r) => (
                  <li key={r.id || r._id}>
                    <span className={r.status === "sent" ? "text-emerald-400" : "text-red-400"}>
                      {r.status}
                    </span>{" "}
                    · {r.id || r._id}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </Panel>
  );
}
